import { Button, Divider, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import { BbUserRewardSummary, bbGetUserRewardSummary } from '../../../services/blindBoxService';

function NumRow({ label, value }: { label: string; value: number | string }) {
  return (
    <Stack direction="row" spacing={2} alignItems="baseline">
      <Typography variant="body2" sx={{ minWidth: 140, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="subtitle2">{value}</Typography>
    </Stack>
  );
}

export function SectionUserRewardSummary({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const [userId, setUserId] = useState('');
  const [data, setData] = useState<BbUserRewardSummary | null>(null);

  const load = useCallback(() => {
    if (!userId) return;
    bbGetUserRewardSummary(userId)
      .then(setData)
      .catch((e: any) => notify(e?.message || 'Error', { type: 'error' }));
  }, [userId, notify]);

  useEffect(() => {
    setData(null);
  }, [userId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          label="userId (required)"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          sx={{ minWidth: 320 }}
        />
        <Button onClick={load} disabled={!userId}>
          {t('module.blindBox.refresh')}
        </Button>
      </Stack>

      {!data ? null : (
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle1">{t('module.blindBox.userRewardSummaryBasic')}</Typography>
            <NumRow label="userId" value={data.userId} />
            <NumRow label="walletAddress" value={data.walletAddress || '—'} />
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle1">{t('module.blindBox.userRewardSummarySpins')}</Typography>
            <NumRow label={t('module.blindBox.totalEarned')} value={data.spins.totalEarned} />
            <NumRow label={t('module.blindBox.pending')} value={data.spins.pending} />
            <NumRow label={t('module.blindBox.distributed')} value={data.spins.distributed} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>taskSlug</TableCell>
                    <TableCell>earned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.spins.byTask || []).map((r) => (
                    <TableRow key={r.taskSlug}>
                      <TableCell>{r.taskSlug}</TableCell>
                      <TableCell>{r.earned}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
              {t('module.blindBox.userRewardSummaryByWin')}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('module.blindBox.userRewardSummaryRewardName')}</TableCell>
                    <TableCell>{t('module.blindBox.userRewardSummaryEarned')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.spins.byWin || []).map((r, i) => (
                    <TableRow key={`${r.rewardName}-${i}`}>
                      <TableCell>{r.rewardName}</TableCell>
                      <TableCell>{r.earned}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle1">{t('module.blindBox.userRewardSummaryBoxes')}</Typography>
            <NumRow label={t('module.blindBox.totalEarned')} value={data.boxes.totalEarned} />
            <NumRow label={t('module.blindBox.pending')} value={data.boxes.pending} />
            <NumRow label={t('module.blindBox.distributed')} value={data.boxes.distributed} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>taskSlug</TableCell>
                    <TableCell>earned</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.boxes.byTask || []).map((r) => (
                    <TableRow key={r.taskSlug}>
                      <TableCell>{r.taskSlug}</TableCell>
                      <TableCell>{r.earned}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
              {t('module.blindBox.userRewardSummaryByWin')}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('module.blindBox.userRewardSummaryRewardName')}</TableCell>
                    <TableCell>{t('module.blindBox.userRewardSummaryEarned')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.boxes.byWin || []).map((r, i) => (
                    <TableRow key={`${r.rewardName}-${i}`}>
                      <TableCell>{r.rewardName}</TableCell>
                      <TableCell>{r.earned}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle1">{t('module.blindBox.userRewardSummaryXp')}</Typography>
            <NumRow label={t('module.blindBox.totalEarned')} value={data.xp.totalEarned} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>source</TableCell>
                    <TableCell>totalXp</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.xp.bySource || []).map((r) => (
                    <TableRow key={r.source}>
                      <TableCell>{r.source}</TableCell>
                      <TableCell>{r.totalXp}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle1">{t('module.blindBox.userRewardSummaryLotteryTickets')}</Typography>
            <NumRow label={t('module.blindBox.total')} value={data.lotteryTickets.totalTickets} />

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>source</TableCell>
                    <TableCell>count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.lotteryTickets.bySource || []).map((r) => (
                    <TableRow key={r.source}>
                      <TableCell>{r.source}</TableCell>
                      <TableCell>{r.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>

          {data.wins ? (
            <>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle1">{t('module.blindBox.userRewardSummaryWins')}</Typography>
                <NumRow label={t('module.blindBox.totalWins')} value={data.wins.totalWins} />
                {(data.wins.bySource || []).map((src) => (
                  <Stack key={src.source} spacing={0.5} sx={{ pl: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {src.source} — {t('module.blindBox.totalWins')}: {src.totalWins}
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>rewardCategory</TableCell>
                            <TableCell>count</TableCell>
                            <TableCell>totalAmount</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(src.byCategory || []).map((c) => (
                            <TableRow key={`${src.source}-${c.rewardCategory}`}>
                              <TableCell>{c.rewardCategory}</TableCell>
                              <TableCell>{c.count}</TableCell>
                              <TableCell>{c.totalAmount}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                ))}
              </Stack>
            </>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
}

